import React, { useState } from 'react';
import { FaShareAlt, FaRegCopy, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';

const JoiningInfo = ({ shareLink }) => {
  const [showFallback, setShowFallback] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  const canBrowserShare = () =>
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    (navigator.canShare ? navigator.canShare({ url: shareLink }) : true);

  const handleShareClick = async () => {
    try {
      if (isNative) {
        // Prefer Capacitor Share on device
        const { value: canShare } = await Share.canShare();
        if (canShare) {
          await Share.share({
            title: 'Meeting Invitation',
            text: 'Join my meeting using this link:',
            url: shareLink,
            dialogTitle: 'Share meeting link',
          });
          return;
        }
      }

      // Web fallback (mobile/desktop browser)
      if (canBrowserShare()) {
        await navigator.share({
          title: 'Meeting Invitation',
          text: 'Join my meeting using this link:',
          url: shareLink,
        });
      } else {
        setShowFallback(true);
      }
    } catch (err) {
      // Ignore user cancel; show fallback for other errors
      if (err?.name !== 'AbortError') setShowFallback(true);
    }
  };

  const copyLink = async () => {
    try {
      if (isNative) {
        await Clipboard.write({ string: shareLink });
      } else {
        await navigator.clipboard.writeText(shareLink);
      }
      toast.success('Link copied to clipboard!');
      setShowFallback(false);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-800 text-white overflow-hidden">
      <div className="p-4">
        <h3 className="text-sm font-medium mb-2">Joining info</h3>
        <div className="bg-gray-100 p-2 rounded text-gray-800 text-sm break-all">{shareLink}</div>

        <div className="mt-4 space-y-2">
          <button
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
            onClick={copyLink}
          >
            <FaRegCopy size={16} />
            Copy joining info
          </button>

          <button
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
            onClick={handleShareClick}
          >
            <FaShareAlt />
            Share
          </button>

          {showFallback && (
            <div className="fallback-modal">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Share Meeting Link</h3>
                  <button onClick={() => setShowFallback(false)} className="close-button">
                    <FaTimes />
                  </button>
                </div>
                <div className="link-display">
                  <p className="link-text">{shareLink}</p>
                  <button onClick={copyLink} className="copy-button">
                    <FaRegCopy /> Copy
                  </button>
                </div>
                <p className="instructions">The link has been copied. You can now paste it into any app.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .joining-info-container {
          max-width: 500px;
          margin: 0 auto;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .link-display {
          display: flex;
          align-items: center;
          margin-bottom: 15px;
          background: white;
          padding: 10px 15px;
          border-radius: 6px;
          border: 1px solid #dee2e6;
        }
        
        .link-text {
          flex: 1;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 14px;
          color: #495057;
        }
        
        .copy-button, .share-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .copy-button {
          background: #e9ecef;
          color: #495057;
          margin-left: 10px;
        }
        
        .copy-button:hover {
          background: #dee2e6;
        }
        
        .share-button {
          width: 100%;
          background: #0d6efd;
          color: white;
        }
        
        .share-button:hover {
          background: #0b5ed7;
        }
        
        .fallback-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 8px;
          width: 90%;
          max-width: 400px;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 18px;
          color: #212529;
        }
        
        .close-button {
          background: none;
          border: none;
          font-size: 18px;
          color: #6c757d;
          cursor: pointer;
        }
        
        .instructions {
          margin-top: 15px;
          font-size: 14px;
          color: #6c757d;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default JoiningInfo;
